import React, { useState, useMemo } from 'react'
import Icon from 'components/Icon'
import useApi, { request } from 'hooks/useApi'
import { Card, Button } from '@arnsbogroup/react-voicearchive'
import classNames from 'classnames'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'

export default ({ project: { serviceIds, details: { productType } }, dispatch }) => {
  const { formatMessage } = useIntl()
  const [showAll, setShowAll] = useState(false)
  const [_services] = useApi('/rest/v2/service', {
    load: true,
  })

  const [services, recommendedServiceIds] = useMemo(() => {
    if (!_services) {
      return [null, []]
    }

    const recommendedServiceIds = _services
      .filter(({ products = [] }) => products.some(({ slug }) => slug == productType))
      .map(({ id }) => id)

    const nextServices = _services
      .sort((a, b) => Number(serviceIds.includes(+b.id)) - Number(serviceIds.includes(+a.id)))
      .filter(({ id }) => showAll || serviceIds.includes(+id) || recommendedServiceIds.includes(+id))

    return [nextServices, recommendedServiceIds]
  }, [showAll, _services, productType])

  const onSelectRecommendedServiceIds = () => {
    dispatch({
      type: 'SET_SERVICE_IDS',
      payload: recommendedServiceIds,
    })
  }

  const onToggleServiceId = (id) => {
    const nextServiceIds = serviceIds.includes(id) ?
      serviceIds.filter((_id) => _id !== id) :
      [...serviceIds, id]

    dispatch({
      type: 'SET_SERVICE_IDS',
      payload: nextServiceIds,
    })
  }

  if (!services) {
    return null
  }

  return (
    <section className="space-y-4">
      <h1 className="text-base font-medium text-sm space-x-2">
        <span className="text-gray-700">
          2. Select services <span className="font-normal text-gray-400 italic">Optional</span>
        </span>
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        { services.map(({ id, name, description }) => (
              <Card
                key={id}
                onClick={() => onToggleServiceId(+id)}
                className={
                  classNames('group border-2 p-4 flex items-center justify-between cursor-pointer', {
                    'border-dashed border-gray-300 hover:border-primary': !serviceIds.includes(+id),
                    'border-primary': serviceIds.includes(+id),
                    'opacity-50 hover:opacity-100': !showAll && recommendedServiceIds.includes(+id) && !serviceIds.includes(+id),
                  })
                }
              >
                <div className="text-sm h-full">
                  <h1 className="font-medium">
                    { `${id}.title` in messages ? formatMessage(messages[`${id}.title`]) : name }
                  </h1>
                  <p className="line-clamp-2 group-hover:line-clamp-none">
                    { `${id}.description` in messages ? formatMessage(messages[`${id}.description`]) : description }
                  </p>
                </div>
                <div className="text-primary">
                  { serviceIds.includes(+id) ? (
                    <Icon icon="check" />
                  ) : (
                    <Icon icon="plus" className="hidden group-hover:inline" />
                  ) }
                </div>
              </Card>
            ))
        }

        <Card className="cursor-pointer w-full text-gray-600 text-gray-600 border-gray-100 bg-gray-100 hover:bg-gray-200 hover:text-primary text-sm font-medium flex items-center justify-center" onClick={() => setShowAll(!showAll)}>
          { showAll ? (
            <span><FormattedMessage {...messages.showOnlyRecommended}/><Icon icon="chevron-up" /></span>
          ) : (
            <span><FormattedMessage {...messages.showAll}/><Icon icon="chevron-down" /></span>
          ) }
        </Card>
      </div>

      <div>
        { !recommendedServiceIds.every((id) => serviceIds.includes(+id)) &&
          <Button size="sm" variant="text" onClick={onSelectRecommendedServiceIds}>
            <FormattedMessage {...messages.selectAllRecommended}/><Icon icon="plus" />
          </Button>
        }
      </div>
    </section>
  )
}

